import React, { useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Box, TextButton } from '@wix/design-system';
import ConfirmDeviceIllustration from '../../../../assets/images/confirmDevice.svg';
import DeniedIllustration from '../../../../assets/images/denied.svg';
import { AppContextProvider } from '../../../AppLoader';
import { ThemedText } from '../../../ThemedComponents';
import { ResendVerificationCode } from '../../ResendVerificationCode';
import { ApprovalChallengeStatus } from '../../../../stores/premiumUsers2FaEnforcement/ownerApp/types';

/**
 * confirmation step (pending) of the owner-app add-auth wizard (wix-2025), shown
 * after the push is sent. Waits for approval: starts the resend timer and stops polling
 * on unmount.
 */
export const OwnerAppConfirmPendingBody: React.FC = observer(() => {
  const {
    rootStore: {
      postLoginStore: {
        premiumUsers2FaEnforcementStore: { ownerAppStore: store },
      },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  useEffect(() => {
    store.resendSuccessfullyIndication = true;
    return () => {
      store.stopPolling();
    };
  }, [store]);
  const name = store.selectedDevice?.name;
  return (
    <Box
      direction="vertical"
      width="100%"
      gap={4}
      align="center"
      style={{ textAlign: 'center' }}
    >
      <Box width={96} align="center">
        <ConfirmDeviceIllustration />
      </Box>
      <Box direction="vertical" gap={1} align="center">
        <ThemedText size="medium" weight="normal">
          {t('addOwnerApp.wizard.confirm.label.title')}
        </ThemedText>
        <ThemedText size="small">
          {t('addOwnerApp.wizard.confirm.label', { deviceName: `${name}` })}
        </ThemedText>
        <ThemedText size="small">
          {t('addOwnerApp.wizard.confirm.message')}
        </ThemedText>
      </Box>
      <ResendVerificationCode
        sendConfirmationCode={() => store.sendPushToSelectedDevice(true)}
        resendSuccessfullyIndication={store.resendSuccessfullyIndication}
        resendThrottleTimerValue={store.resendThrottleTimerValue}
        resendErrorMessage={store.resendErrorMessage}
        resendButtonTranslationKey="addOwnerApp.wizard.confirm.resend-link"
        resendIndicationTranslationKey="request.resend.indication"
        resendButtonSize="medium"
      />
    </Box>
  );
});

/**
 * confirmation step (error) of the owner-app add-auth wizard (wix-2025) — the
 * push timed out or was declined; offers a resend link.
 */
export const OwnerAppConfirmErrorBody: React.FC = observer(() => {
  const {
    rootStore: {
      postLoginStore: {
        premiumUsers2FaEnforcementStore: { ownerAppStore: store },
      },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  const isExpired = store.authStatus === ApprovalChallengeStatus.EXPIRED;
  return (
    <Box
      direction="vertical"
      width="100%"
      gap={4}
      align="center"
      style={{ textAlign: 'center' }}
    >
      <Box width={96} align="center">
        <DeniedIllustration />
      </Box>
      <Box direction="vertical" gap={1} align="center">
        <ThemedText size="medium" weight="normal">
          {t(
            isExpired
              ? 'addOwnerApp.wizard.timeout.title'
              : 'addOwnerApp.wizard.denied.title',
          )}
        </ThemedText>
        <ThemedText size="small" weight="thin">
          {t(
            isExpired
              ? 'addOwnerApp.wizard.timeout.message'
              : 'addOwnerApp.wizard.denied.message',
          )}
        </ThemedText>
      </Box>
      <TextButton
        size="medium"
        weight="thin"
        onClick={() => store.sendPushToSelectedDevice(true, store.authStatus)}
      >
        {t('addOwnerApp.wizard.timeout.resend-link')}
      </TextButton>
    </Box>
  );
});
